import React, { createContext, useContext, useState } from 'react';

// Notification Context
const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Add a new notification
  const addNotification = (notification) => {
    const newNotification = {
      id: Date.now() + Math.random(),
      type: notification.type || 'info',
      title: notification.title,
      message: notification.message,
      timestamp: new Date(),
      read: false,
      data: notification.data || null
    };

    setNotifications(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);

    // Announce notification to screen reader if enabled
    announceNotification(newNotification);
  };

  // Announce notification to screen reader
  const announceNotification = (notification) => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const isScreenReaderEnabled = localStorage.getItem('accessibility-screen-reader') === 'true';

      if (isScreenReaderEnabled && !speechSynthesis.speaking) {
        const utterance = new SpeechSynthesisUtterance(`${notification.title}. ${notification.message}`);
        utterance.rate = 1.0;
        utterance.pitch = 1.1;
        utterance.volume = 0.9;

        // Slight delay to ensure notification is rendered
        setTimeout(() => {
          speechSynthesis.speak(utterance);
        }, 100);
      }
    }
  };

  // Mark a notification as read
  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Mark all notifications as read
  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, read: true }))
    );
    setUnreadCount(0);
  };

  // Remove a notification
  const removeNotification = (id) => {
    setNotifications(prev => {
      const notification = prev.find(n => n.id === id);
      const newNotifications = prev.filter(n => n.id !== id);

      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }

      return newNotifications;
    });
  };

  // Clear all notifications
  const clearAllNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);

    localStorage.removeItem('gallery_notifications_processed');
    localStorage.removeItem('news_notifications_processed');
    localStorage.removeItem('self_registered_notifications_processed');
  };

  // Check if a notification with specific data exists
  const hasNotificationWithData = (dataKey, dataValue) => {
    return notifications.some(notification =>
      notification.data && notification.data[dataKey] === dataValue
    );
  };

  // Get notifications by type
  const getNotificationsByType = (type) => {
    return notifications.filter(notification => notification.type === type);
  };

  // Get unread notifications
  const getUnreadNotifications = () => {
    return notifications.filter(notification => !notification.read);
  };

  // Get unread count by type
  const getUnreadCountByType = (type) => {
    return notifications.filter(notification => notification.type === type && !notification.read).length;
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAllNotifications,
        hasNotificationWithData,
        getNotificationsByType,
        getUnreadNotifications,
        getUnreadCountByType
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
